import { getRouter } from './routes';
import ThemeProvider from './theme';
import ThemeSettings from './components/settings';
import { ChartStyle } from './components/chart';
import { ProgressBarStyle } from './components/ProgressBar';
import NotistackProvider from './components/NotistackProvider';
import MotionLazyContainer from './components/animate/MotionLazyContainer';
import { EventBusProvider } from './event-bus';
import { FhirClientProvider } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { createAxiosFhirClient } from 'src/careflow/ph-core/fhir-client/fhir-base/clients/axios';
import { DialogRenderer } from './stores/dialog';
import { RouterProvider } from 'react-router';
import { createAxiosIndexedFhirClient } from 'src/careflow/ph-core/fhir-client/fhir-base/clients/axios-indexed';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './crs/firebase/firebase-init';
import { DashboardRegistryProvider } from './careflow/care-coordination/dashboard/context/DashboardContext';
import { useTenant } from './careflow/ph-core/multi-tenant/tenant/context/TenantProvider';
import axiosFhirInstance from './careflow/ph-core/fhir-client/application/adapters/out/instance/axiosFhirInstance';
import axiosIndexedFhirInstance from './careflow/ph-core/fhir-client/application/adapters/out/instance/axiosIndexedFhirInstance';
import {
  getInitialPatientContext,
  PatientContext,
} from './careflow/ph-core/fhir-client/application/adapters/in/ui/contexts';
import { PatientContextProvider } from './careflow/ph-core/fhir-client/application/adapters/in/ui/contexts/PatientContext';
import StandardDialogProvider from './careflow/ph-core/ui/base/StandardDialogProvider';
import RefreshAuthWatcherProvider from './careflow/ph-core/ui/base/RefreshAuthWatcherProvider';
import RefreshSSO from './careflow/ph-core/ui/base/RefreshSSO';
import StandardLoadingBackdropProvider from './careflow/ph-core/ui/base/StandardLoadingBackdropProvider';
import AuthenticatedIcomxChatProvider from './sections/crs/patient/components/icomx/AuthenticatedIcomxChatProvider';
import { NicheAimTimeTrackingProvider } from './components/nicheaim-time-tracking/NicheAimTimeTrackingProvider';
import NicheAimTimeTrackingWidget from './sections/crs/patient/components/nicheaim-time-tracking/NicheAimTimeTrackingWidget';

export const fhirClient = createAxiosFhirClient(axiosFhirInstance);
export const indexedFhirClient = createAxiosIndexedFhirClient(
  axiosIndexedFhirInstance,
  axiosFhirInstance
);

const patientContext = ((): PatientContext => {
  const { patient, admissionDetails, careTeam } = getInitialPatientContext();
  return {
    patient: {
      ...patient,
      id: '81d5f8c4-f0bb-49ee-9ee2-183d30160dc2',
    },
    admissionDetails: {
      ...admissionDetails,
      id: '854c06de-af63-44dc-89e7-c7ffbac0e8ad',
    },
    careTeam: {
      ...careTeam,
      id: '078a5765-dfd7-41db-8d44-90ee7701c1c4',
    },
  };
})();

export default function App() {
  const tenant = useTenant();

  function getMainRender() {
    const router = getRouter(tenant.frontendBaseUrl, tenant.backendBaseUrl);

    return (
      <ThemeProvider>
        <StandardDialogProvider>
          <RefreshAuthWatcherProvider>
            <RefreshSSO>
              <FhirClientProvider
                clients={{
                  default: fhirClient,
                  indexed: indexedFhirClient,
                }}
              >
                <MotionLazyContainer>
                  <ThemeSettings>
                    <EventBusProvider>
                      <NotistackProvider>
                        <StandardLoadingBackdropProvider>
                          <DialogRenderer />
                          <ProgressBarStyle />
                          <ChartStyle />
                          <PatientContextProvider value={patientContext}>
                            <DashboardRegistryProvider>
                              <NicheAimTimeTrackingProvider router={router}>
                                <AuthenticatedIcomxChatProvider router={router}>
                                  <RouterProvider router={router} />
                                </AuthenticatedIcomxChatProvider>
                                <NicheAimTimeTrackingWidget />
                              </NicheAimTimeTrackingProvider>
                            </DashboardRegistryProvider>
                          </PatientContextProvider>
                        </StandardLoadingBackdropProvider>
                      </NotistackProvider>
                    </EventBusProvider>
                  </ThemeSettings>
                </MotionLazyContainer>
              </FhirClientProvider>
              <ReactQueryDevtools />
            </RefreshSSO>
          </RefreshAuthWatcherProvider>
        </StandardDialogProvider>
      </ThemeProvider>
    );
  }

  return tenant.isLoading ? <>Loading...</> : getMainRender();
}
